import { Box, Button, Modal, Tabs, Tab } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeScheduleDto, ScheduleRequestDto } from "../../services/dto/response/schedule-response-dto";
import { useToast } from "../../context/toast-context";
import { ScheduleService } from "../../services/schedule-service";
import { AddScheduleForm } from "../form/add-schedule-form";
import { Branch, Schedule } from "../../types";
import { ScheduleIcon } from "../icons/ScheduleIcon";
import { AssignEmployeesModal } from "./assign-employees-modal";
import { DeleteScheduleButton } from "../schedule/DeleteScheduleButton";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75vw",
  height: "75vh",
  maxWidth: "none",
  maxHeight: "none",
  overflowY: "auto",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

const TABS = {
  VIEW: 0,
  ADD: 1,
  EDIT: 2,
}

interface ViewSchedulesProps {
  branchId: number;
  selectedBranch: Branch;
  onAddSchedule?: (branchId: number, schedules: ScheduleRequestDto[]) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`schedule-tabpanel-${index}`}
      aria-labelledby={`schedule-tab-${index}`}
      {...other}
    >
      {value === index && <div className="p-8">{children}</div>}
    </div>
  );
}

export function ViewSchedules({ branchId, selectedBranch, onAddSchedule }: ViewSchedulesProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssignEmployeesModalOpen, setIsAssignEmployeesModalOpen] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast();
  const scheduleService = useMemo(() => new ScheduleService(), []);
  const [tabValue, setTabValue] = useState(TABS.VIEW);
  const [scheduleToEdit, setScheduleToEdit] = useState<Schedule | null>(null);
  const [employeesBySchedule, setEmployeesBySchedule] = useState<{[scheduleId: number]: EmployeeScheduleDto[]}>({});
  const [expandedSchedules, setExpandedSchedules] = useState<number[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState<{[scheduleId: number]: boolean}>({});

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue !== TABS.EDIT) {
      setScheduleToEdit(null);
    }
  };

  const toggleAccordion = (scheduleId: number) => {
    setExpandedSchedules(prev => 
      prev.includes(scheduleId) 
        ? prev.filter(id => id !== scheduleId)
        : [...prev, scheduleId]
    );
  };

  const fetchEmployeesForSchedule = async (scheduleId: number) => {
    if (loadingEmployees[scheduleId]) return;
    
    setLoadingEmployees(prev => ({ ...prev, [scheduleId]: true }));
    
    try {
      await scheduleService.getActiveEmployeesByScheduleId(
        { scheduleId },
        (data) => {
          setEmployeesBySchedule(prev => ({
            ...prev,
            [scheduleId]: data
          }));
        },
        (error) => showToast(error, "error")
      );
    } finally {
      setLoadingEmployees(prev => ({ ...prev, [scheduleId]: false }));
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const fetchSchedules = async () => {
      setIsLoading(true);

      try {
        await scheduleService.getSchedules(
          { branchId }, 
          async (data: Schedule[]) => {
            if (!signal.aborted) {
              setSchedules(data);
              if (data.length > 0) {
                for (const schedule of data) {
                  if (!signal.aborted) {
                    await fetchEmployeesForSchedule(schedule.scheduleId);
                  }
                }
              }
            }
          },
          (errorMessage: string) => {
            if (!signal.aborted) {
              showToast(errorMessage, "error");
            }
          }
        );
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    if (isModalOpen) {
      fetchSchedules();
    }
    return () => {
      controller.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast, scheduleService, branchId, isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      setExpandedSchedules([]);
    }
  }, [isModalOpen]);

  const handleAssignEmployees = (scheduleId: number) => {
    setSelectedScheduleId(scheduleId);
    setIsAssignEmployeesModalOpen(true);
  };

  const handleSaveAssignments = async (scheduleId: number, employeeIds: number[]) => {
    await scheduleService.getSchedules(
      { branchId },
      (data: Schedule[]) => {
        setSchedules(data);
        fetchEmployeesForSchedule(scheduleId);
      },
      (error) => showToast(error, "error")
    );
  };

  const handleScheduleDeleted = (scheduleId: number) => {
    setSchedules(schedules.filter((s) => s.scheduleId !== scheduleId));
  }

  const handleEditSchedule = (schedule: Schedule) => {
    setScheduleToEdit(schedule);
    setTabValue(TABS.EDIT);
  };

  const handleScheduleUpdated = async () => {
    await scheduleService.getSchedules(
      { branchId },
      (data: Schedule[]) => {
        setSchedules(data);
      },
      (error) => showToast(error, "error")
    );
    setScheduleToEdit(null);
    setTabValue(TABS.VIEW);
  };

  return (
    <>
      <Button
        variant="contained"
        disableElevation
        sx={{
          margin: 0,
          minWidth: "2.5rem",
          maxWidth: "2.5rem",
          background: "transparent",
          border: "1px solid #EF0BB8",
          borderRadius: "50%",
          color: "#EF0BB8",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          width: "2.5rem",
          height: "2.5rem",
          padding: "8px",
        }}
        onClick={() => setIsModalOpen(true)}
        title={t("branches.view.schedules")}
      >
        <ScheduleIcon />
      </Button>
      {isModalOpen && (
        <Modal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setScheduleToEdit(null);
            setTabValue(TABS.VIEW);
            setExpandedSchedules([]);
          }}
          aria-labelledby={t("branches.view.schedules")}
          aria-describedby={t("branches.view.schedules")}
        >
          <Box sx={style} className="custom-scrollbar">
            <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2 border-b border-gray-200">
              <h1 className="font-montserrat font-medium">
                {t("branches.view.schedules")}
              </h1>
            </div>

            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTabs-indicator": {
                  backgroundColor: "#EF0BB8",
                },
                "& .MuiTab-root": {
                  fontFamily: "Montserrat",
                  textTransform: "none",
                  color: "#666",
                  "&.Mui-selected": {
                    color: "#EF0BB8",
                  },
                },
              }}
            >
              <Tab label={t("branches.view.schedules")} />
              <Tab label={t("branches.add-schedule")} />
              {scheduleToEdit && (
                <Tab label={t("branches.schedules.edit.title")} />
              )}
            </Tabs>

            <TabPanel value={tabValue} index={TABS.VIEW}>
              <div className="flex flex-col gap-6">
                {isLoading && (
                  <div className="w-full flex justify-center items-center py-8">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce"></div>
                    </div>
                  </div>
                )}
                {!isLoading && schedules.length === 0 && (
                  <div className="w-full flex justify-center items-center py-8">
                    <p className="text-gray-500 font-montserrat">
                      {t("branches.view.empty-schedules")}
                    </p>
                  </div>
                )}
                {!isLoading &&
                  schedules.length > 0 &&
                  schedules.map((schedule, index) => (
                    <div
                      key={index}
                      className="flex flex-col w-full bg-white rounded-xl border border-gray-200 p-6 hover:border-[#EF0BB8] transition-colors duration-200"
                    >
                      <div className="flex w-full items-start gap-4">
                        <div className="flex flex-col items-start justify-center flex-1">
                          <div className="flex justify-between items-center w-full mb-4">
                            <h3 className="font-montserrat font-semibold text-lg text-gray-800">
                              {schedule.name}
                            </h3>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  handleAssignEmployees(schedule.scheduleId)
                                }
                                className="px-4 py-2 text-sm bg-[#EF0BB8] text-white rounded-lg hover:bg-[#d009a6] font-montserrat"
                              >
                                {t("branches.schedules.assign-employees.title")}
                              </button>
                              <Button
                                title={t("branches.schedules.edit.title")}
                                onClick={() => handleEditSchedule(schedule)}
                                sx={{
                                  margin: 0,
                                  minWidth: "fit-content",
                                  background: "transparent",
                                  border: "1px solid #EF0BB8",
                                  borderRadius: "50%",
                                  color: "#EF0BB8",
                                  textTransform: "none",
                                  fontFamily: "Montserrat",
                                  fontWeight: 300,
                                  width: "2.5rem",
                                  height: "2.5rem",
                                }}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <g fill="#EF0BB8">
                                    <path d="M8.293 3.707 1 11v4h4l7.293-7.293-4-4ZM9.707 2.293l4 4 1.465-1.465a2.829 2.829 0 0 0-4-4L9.707 2.293Z" />
                                  </g>
                                </svg>
                              </Button>
                              <DeleteScheduleButton
                                scheduleId={schedule.scheduleId}
                                onScheduleDeleted={() =>
                                  handleScheduleDeleted(schedule.scheduleId)
                                }
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                            {schedule.schedulesDays.map((day, dayIndex) => (
                              <div
                                key={dayIndex}
                                className="flex flex-col items-start justify-center bg-gray-50 rounded-lg p-4"
                              >
                                <p className="font-montserrat font-medium text-sm text-gray-700 mb-2">
                                  {t(`days.${day.dayOfWeek}`)}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {day.schedulesBlocks.map(
                                    (block, blockIndex) => (
                                      <span
                                        key={blockIndex}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-montserrat bg-[#EF0BB8] bg-opacity-10 text-[#EF0BB8]"
                                      >
                                        {block.startTime.substring(0, 5)} - {block.endTime.substring(0, 5)}
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {/* Acordeón para mostrar los empleados */}
                      <div className="mt-4 border-t pt-2">
                        <button 
                          onClick={() => toggleAccordion(schedule.scheduleId)}
                          className={`flex items-center justify-between w-full px-4 py-2 text-sm font-montserrat text-left text-gray-600 hover:text-[#EF0BB8] focus:outline-none ${
                            expandedSchedules.includes(schedule.scheduleId) ? "text-[#EF0BB8]" : ""
                          }`}
                        >
                          <span>Empleados asignados</span>
                          <svg 
                            className={`w-5 h-5 text-gray-600 transform transition-transform duration-200 ${
                              expandedSchedules.includes(schedule.scheduleId) ? "rotate-180 text-[#EF0BB8]" : ""
                            }`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24" 
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </button>
                        
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out transform ${
                            expandedSchedules.includes(schedule.scheduleId)
                              ? "max-h-[1000px] opacity-100 scale-100"
                              : "max-h-0 opacity-0 scale-95"
                          }`}
                        >
                          <div className="px-4 py-2 bg-gray-50 rounded-lg mt-2">
                            {loadingEmployees[schedule.scheduleId] ? (
                              <div className="flex justify-center py-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                  <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                  <div className="w-2 h-2 bg-[#EF0BB8] rounded-full animate-bounce"></div>
                                </div>
                              </div>
                            ) : employeesBySchedule[schedule.scheduleId]?.length > 0 ? (
                              <ul className="list-disc pl-5">
                                {employeesBySchedule[schedule.scheduleId].map((employee, idx) => (
                                  <li key={idx} className="text-sm font-montserrat text-gray-700 mb-1">
                                    {employee.name}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm font-montserrat text-gray-500 text-center py-2">
                                No hay empleados asignados a este horario
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </TabPanel>

            <TabPanel value={tabValue} index={TABS.ADD}>
              <AddScheduleForm
                selectedBranch={selectedBranch}
                setIsModalOpen={setIsModalOpen}
                onAddSchedule={onAddSchedule}
                onSuccess={() => {
                  setTabValue(TABS.VIEW);
                  scheduleService.getSchedules(
                    { branchId },
                    (data: Schedule[]) => {
                      setSchedules(data);
                    },
                    (error) => showToast(error, "error")
                  );
                }}
              />
            </TabPanel>

            {scheduleToEdit && (
              <TabPanel value={tabValue} index={TABS.EDIT}>
                <AddScheduleForm
                  selectedBranch={selectedBranch}
                  setIsModalOpen={setIsModalOpen}
                  onAddSchedule={onAddSchedule}
                  scheduleToEdit={scheduleToEdit}
                  onScheduleUpdated={() => {
                    handleScheduleUpdated();
                    setTabValue(TABS.VIEW);
                  }}
                />
              </TabPanel>
            )}
          </Box>
        </Modal>
      )}
      {selectedScheduleId && (
        <AssignEmployeesModal
          isOpen={isAssignEmployeesModalOpen}
          onClose={() => {
            setIsAssignEmployeesModalOpen(false);
            setSelectedScheduleId(null);
          }}
          scheduleId={selectedScheduleId}
          onAssignEmployees={handleSaveAssignments}
        />
      )}
    </>
  );
} 